import React, { useRef, useState } from 'react';
import { useApp } from '../../context/AppContext';
import Thumb from '../../components/Thumb';
import ScanInput from '../../components/ScanInput';
import CameraScanner from '../../components/CameraScanner';
import { RotateCcw, CheckCircle2, Camera, PackageCheck } from 'lucide-react';
import { toast } from 'sonner';

/**
 * "Return" tab — check-in flow for gear that's currently out on this project.
 * Each row exposes a "Return 1" button; same BT-scanner path is used under
 * the hood. Camera scanner is available as a field-ready fallback (wrap day,
 * BT scanner not handy). "Return All" wraps everything in one shot post-shoot.
 */
export default function ReturnTab({ project }) {
  const { items, expandProjectGear, scanForProject, projectReturnAll } = useApp();
  const scannerRef = useRef(null);
  const [cameraOpen, setCameraOpen] = useState(false);
  const [returningAll, setReturningAll] = useState(false);

  const expanded = expandProjectGear(project);
  const out = expanded.filter((e) => (project.packed?.[e.itemCode] || 0) > 0);
  const totalOut = out.reduce((s, e) => s + (project.packed?.[e.itemCode] || 0), 0);
  const isComplete = totalOut === 0;

  if (out.length === 0) {
    return (
      <div className="surface rounded-lg p-10 text-center">
        <RotateCcw size={28} className="mx-auto text-neutral-700 mb-3" />
        <p className="text-sm text-neutral-400">Nothing checked out for this project right now.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="surface rounded-lg p-4 flex items-center justify-between gap-3 flex-wrap">
        <div className="text-[11px] tracking-wider uppercase text-neutral-500">
          On set · {totalOut} unit{totalOut === 1 ? '' : 's'} out
        </div>
        <button
          onClick={async () => {
            if (!window.confirm(
              `Return all ${totalOut} unit${totalOut === 1 ? '' : 's'} at once?\n\n` +
              `Every packed unit will be checked in and any Media cards will move to "Needs Offload".`
            )) return;
            setReturningAll(true);
            try {
              await projectReturnAll(project.id);
              toast.success(`Returned ${totalOut} unit${totalOut === 1 ? '' : 's'}`);
            } catch (e) {
              toast.error(e.message || 'Could not return all');
            } finally {
              setReturningAll(false);
            }
          }}
          disabled={returningAll}
          className="text-[11px] px-3 py-1.5 rounded-md border border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10 flex items-center gap-1.5 disabled:opacity-50"
          data-testid="return-all-btn"
        >
          <PackageCheck size={13} /> {returningAll ? 'Returning…' : `Return All (${totalOut})`}
        </button>
      </div>

      <div className="flex items-stretch gap-2">
        <div className="flex-1">
          <ScanInput
            ref={scannerRef}
            direction="return"
            isComplete={isComplete}
            onScan={(code, dir) => scanForProject(project.id, code, dir || 'return')}
          />
        </div>
        <button
          onClick={() => setCameraOpen(true)}
          title="Use this device's camera as a QR scanner — handy for wrap day on location"
          className="shrink-0 px-3 rounded-md border border-neutral-800 text-neutral-300 hover:text-amber-300 hover:border-amber-500/40 hover:bg-amber-500/5 flex items-center gap-1.5 text-[11px] transition-colors"
          data-testid="return-camera-btn"
        >
          <Camera size={13} />
          <span className="hidden sm:inline">Use camera</span>
        </button>
      </div>

      <CameraScanner
        open={cameraOpen}
        onClose={() => setCameraOpen(false)}
        onDetected={(code) => {
          // Returning, so explicitly send 'return' direction.
          scannerRef.current?.submitCode(code, 'return');
        }}
      />

      <div className="surface rounded-lg overflow-hidden divide-y divide-neutral-900">
        {out.map((e) => {
          const it = items.find((i) => i.code === e.itemCode);
          if (!it) return null;
          const packed = project.packed?.[e.itemCode] || 0;
          return (
            <div key={e.itemCode} className="px-4 py-2.5 flex items-center gap-3 row-hover">
              <Thumb src={it.image} name={it.name} />
              <div className="flex-1 min-w-0">
                <div className="text-sm text-white">{it.name}</div>
                <div className="text-[11px] text-neutral-500 font-mono">{it.code}</div>
              </div>
              <div className="text-[11px] text-neutral-400 mr-2">{packed} out</div>
              <button
                onClick={() => scannerRef.current?.submitCode(e.itemCode, 'return')}
                className="text-[11px] px-2.5 py-1 rounded-md border border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/10 flex items-center gap-1"
              >
                <CheckCircle2 size={11} /> Return 1
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}
